// Rol ve Yetki Yönetimi — plan §4.7.5, M8-S2.
// Matrix: 33+ permission × N rol. Sistem rolleri read-only, custom rol oluştur/güncelle/sil.

import { useEffect, useMemo, useState } from "react";
import type {
  CreateRoleRequest,
  Permissions,
  RoleRow,
  RolesListResponse,
  UpdateRoleRequest,
} from "@mahir/shared";
import { getPermissionLabel, PERMISSION_GROUPS, roleDisplayName } from "@mahir/shared";
import { api, ApiError } from "../../api/client";
import { SUPERADMIN_ONLY_HINT, permissionLockedFor } from "../../lib/role-ui";
import { useAuthStore } from "../../stores/auth-store";

export function RolesList(): JSX.Element {
  const [data, setData] = useState<RolesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftPermissions, setDraftPermissions] = useState<Permissions>({});
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    api<RolesListResponse>("/admin/roles")
      .then((res) => {
        if (!aborted) {
          setData(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [refreshTick]);

  const groupedPerms = useMemo(() => {
    if (!data) return [];
    return PERMISSION_GROUPS.map((g) => ({
      ...g,
      keys: data.permissionKeys.filter((k) => k.startsWith(g.prefix)).sort(),
    })).filter((g) => g.keys.length > 0);
  }, [data]);

  const startEdit = (role: RoleRow): void => {
    setEditingId(role.id);
    setDraftPermissions({ ...role.permissions });
    setDraftName(role.name);
    setDraftDescription(role.description ?? "");
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setDraftPermissions({});
    setDraftName("");
    setDraftDescription("");
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId) return;
    const body: UpdateRoleRequest = {
      name: draftName,
      description: draftDescription || undefined,
      permissions: draftPermissions,
    };
    try {
      await api(`/admin/roles/${editingId}`, { method: "PATCH", body });
      cancelEdit();
      setRefreshTick((t) => t + 1);
    } catch (err: unknown) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Güncellenemedi");
    }
  };

  const deleteRole = async (role: RoleRow): Promise<void> => {
    const confirmed = window.confirm(`${role.name} rolünü silmek istediğinizden emin misiniz?`);
    if (!confirmed) return;
    try {
      await api(`/admin/roles/${role.id}`, { method: "DELETE" });
      setRefreshTick((t) => t + 1);
    } catch (err: unknown) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Silinemedi");
    }
  };

  const createRole = async (input: CreateRoleRequest): Promise<void> => {
    try {
      await api("/admin/roles", { method: "POST", body: input });
      setCreateOpen(false);
      setRefreshTick((t) => t + 1);
    } catch (err: unknown) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Oluşturulamadı");
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8" style={{ color: "var(--color-text-2)" }}>
        Yükleniyor…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <p style={{ color: "var(--color-error)" }}>
          {error ?? "Roller listelenemedi (system.admin yetkisi gerekli)."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)" }}>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
          >
            ROL / YETKİ
          </div>
          <h1
            className="text-3xl"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
          >
            Rol ve Yetki Yönetimi
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-2)" }}>
            {data.roles.length} rol · {data.permissionKeys.length} permission. Sistem rolleri
            read-only; özel rollere izin eklenebilir/çıkarılabilir.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 rounded-lg font-semibold text-sm"
          style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
        >
          + Yeni Rol
        </button>
      </div>

      {error && (
        <div
          className="max-w-5xl mb-4 p-3 rounded text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      )}

      {/* Matrix */}
      <div
        className="overflow-auto rounded-xl"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <table className="text-sm" style={{ minWidth: "100%" }}>
          <thead>
            <tr style={{ background: "var(--color-bg)" }}>
              <th
                className="text-left px-4 py-3 sticky left-0"
                style={{
                  color: "var(--color-text-3)",
                  fontWeight: 600,
                  background: "var(--color-bg)",
                  minWidth: 260,
                }}
              >
                Permission
              </th>
              {data.roles.map((r) => (
                <th
                  key={r.id}
                  className="px-3 py-2 text-center"
                  style={{ color: "var(--color-text-3)", fontWeight: 600, minWidth: 140 }}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span style={{ fontWeight: 700, color: "var(--color-text)" }}>
                      {editingId === r.id ? (
                        <input
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          className="text-xs px-1 py-0.5 border rounded"
                          style={{ borderColor: "var(--color-border)", width: 100 }}
                        />
                      ) : (
                        roleDisplayName(r.name)
                      )}
                    </span>
                    <span className="flex items-center gap-1 text-xxs">
                      {r.isSystem ? (
                        <span
                          className="px-1 rounded text-xxs"
                          style={{
                            background: "color-mix(in srgb, var(--color-primary) 14%, white)",
                            color: "var(--color-primary)",
                            fontSize: "0.6rem",
                          }}
                        >
                          SİSTEM
                        </span>
                      ) : (
                        <span
                          className="px-1 rounded text-xxs"
                          style={{
                            background: "color-mix(in srgb, var(--color-warning) 8%, white)",
                            color: "var(--color-warning)",
                            fontSize: "0.6rem",
                          }}
                        >
                          ÖZEL
                        </span>
                      )}
                      <span style={{ fontSize: "0.6rem", color: "var(--color-text-3)" }}>
                        {r.userCount} user
                      </span>
                    </span>
                    <div className="flex gap-1">
                      {!r.isSystem && editingId !== r.id && (
                        <>
                          <button
                            onClick={() => startEdit(r)}
                            className="text-xxs px-1.5 py-0.5 rounded"
                            style={{
                              background: "transparent",
                              border: "1px solid var(--color-border)",
                              color: "var(--color-primary)",
                              fontSize: "0.65rem",
                            }}
                          >
                            Düzenle
                          </button>
                          <button
                            onClick={() => void deleteRole(r)}
                            className="text-xxs px-1.5 py-0.5 rounded"
                            style={{
                              background: "transparent",
                              border: "1px solid var(--color-border)",
                              color: "var(--color-error)",
                              fontSize: "0.65rem",
                            }}
                          >
                            Sil
                          </button>
                        </>
                      )}
                      {editingId === r.id && (
                        <>
                          <button
                            onClick={() => void saveEdit()}
                            className="text-xxs px-1.5 py-0.5 rounded"
                            style={{
                              background: "var(--color-success)",
                              color: "white",
                              fontSize: "0.65rem",
                            }}
                          >
                            Kaydet
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="text-xxs px-1.5 py-0.5 rounded"
                            style={{
                              background: "transparent",
                              border: "1px solid var(--color-border)",
                              color: "var(--color-text-3)",
                              fontSize: "0.65rem",
                            }}
                          >
                            İptal
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupedPerms.map((group) => (
              <RenderGroup
                key={group.label}
                group={group}
                roles={data.roles}
                editingId={editingId}
                draftPermissions={draftPermissions}
                setDraftPermissions={setDraftPermissions}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editingId && (
        <div className="mt-4 max-w-5xl">
          <label className="text-xs" style={{ color: "var(--color-text-3)" }}>
            Açıklama (opsiyonel)
          </label>
          <input
            type="text"
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            className="w-full px-3 py-2 rounded border text-sm"
            style={{ borderColor: "var(--color-border)" }}
            placeholder="Rolün kısa açıklaması"
          />
        </div>
      )}

      {createOpen && (
        <CreateRoleModal
          permissionKeys={data.permissionKeys}
          onCancel={() => setCreateOpen(false)}
          onCreate={(input) => void createRole(input)}
        />
      )}
    </div>
  );
}

function RenderGroup({
  group,
  roles,
  editingId,
  draftPermissions,
  setDraftPermissions,
}: {
  group: { label: string; keys: string[] };
  roles: RoleRow[];
  editingId: string | null;
  draftPermissions: Permissions;
  setDraftPermissions: (v: Permissions) => void;
}): JSX.Element {
  const actor = useAuthStore((s) => s.user);
  return (
    <>
      <tr>
        <td
          colSpan={roles.length + 1}
          className="px-4 py-2"
          style={{
            // Grup satırı: brand primary'nin çok hafif tonu — item satırlarından ayrışır
            background: "color-mix(in srgb, var(--color-primary) 8%, var(--color-surface))",
            color: "var(--color-primary)",
            fontWeight: 700,
            fontSize: "0.78rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            borderTop: "2px solid var(--color-primary)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          {group.label}
        </td>
      </tr>
      {group.keys.map((key) => (
        <tr key={key}>
          <td
            className="px-4 py-2 sticky left-0"
            style={{
              borderTop: "1px solid var(--color-border)",
              background: "var(--color-surface)",
            }}
          >
            <div
              style={{
                fontSize: "0.85rem",
                color: "var(--color-text)",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {getPermissionLabel(key)}
            </div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.68rem",
                color: "var(--color-text-3)",
                opacity: 0.7,
                marginTop: 2,
              }}
            >
              {key}
            </div>
          </td>
          {roles.map((r) => {
            const isEditing = editingId === r.id;
            const value = isEditing
              ? (draftPermissions[key] ?? false)
              : (r.permissions[key] ?? false);
            return (
              <td
                key={r.id}
                className="px-3 py-2 text-center"
                style={{ borderTop: "1px solid var(--color-border)" }}
              >
                {isEditing ? (
                  <input
                    type="checkbox"
                    checked={value}
                    disabled={permissionLockedFor(key, actor)}
                    title={permissionLockedFor(key, actor) ? SUPERADMIN_ONLY_HINT : undefined}
                    onChange={(e) =>
                      setDraftPermissions({ ...draftPermissions, [key]: e.target.checked })
                    }
                    style={{ cursor: "pointer" }}
                  />
                ) : value ? (
                  <span style={{ color: "var(--color-success)", fontWeight: 700 }}>✓</span>
                ) : (
                  <span style={{ color: "var(--color-text-3)" }}>·</span>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function CreateRoleModal({
  permissionKeys,
  onCancel,
  onCreate,
}: {
  permissionKeys: string[];
  onCancel: () => void;
  onCreate: (input: CreateRoleRequest) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState<Permissions>({});
  const actor = useAuthStore((s) => s.user);

  const groupedPerms = useMemo(() => {
    return PERMISSION_GROUPS.map((g) => ({
      ...g,
      keys: permissionKeys.filter((k) => k.startsWith(g.prefix)).sort(),
    })).filter((g) => g.keys.length > 0);
  }, [permissionKeys]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={onCancel}
    >
      <div
        className="rounded-xl p-6 max-w-2xl w-full mx-4"
        style={{ background: "var(--color-surface)", maxHeight: "85vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3" style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem" }}>
          Yeni Rol
        </h3>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Rol adı (örn: Raporlama Uzmanı)"
          className="w-full px-3 py-2 rounded border text-sm mb-2"
          style={{ borderColor: "var(--color-border)" }}
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Açıklama (opsiyonel)"
          className="w-full px-3 py-2 rounded border text-sm mb-3"
          style={{ borderColor: "var(--color-border)", minHeight: 60 }}
        />
        <div className="mb-3 text-xs" style={{ color: "var(--color-text-2)" }}>
          {Object.values(perms).filter(Boolean).length} / {permissionKeys.length} izin seçildi
        </div>
        <div className="mb-3 flex gap-2 text-xs">
          <button
            onClick={() => {
              const all: Permissions = {};
              for (const k of permissionKeys) all[k] = true;
              setPerms(all);
            }}
            className="px-2 py-1 rounded border"
            style={{ borderColor: "var(--color-border)" }}
          >
            Tümünü seç
          </button>
          <button
            onClick={() => setPerms({})}
            className="px-2 py-1 rounded border"
            style={{ borderColor: "var(--color-border)" }}
          >
            Temizle
          </button>
        </div>
        <div className="flex flex-col gap-2 mb-4">
          {groupedPerms.map((g) => (
            <details key={g.label} open>
              <summary
                className="cursor-pointer text-xs uppercase tracking-wider"
                style={{ color: "var(--color-text-3)", fontWeight: 600 }}
              >
                {g.label} ({g.keys.filter((k) => perms[k]).length}/{g.keys.length})
              </summary>
              <div className="mt-1 grid grid-cols-2 gap-1 pl-3">
                {g.keys.map((k) => (
                  <label
                    key={k}
                    className="flex items-start gap-1.5 text-xs"
                    style={{ lineHeight: 1.2 }}
                  >
                    <input
                      type="checkbox"
                      checked={perms[k] ?? false}
                      disabled={permissionLockedFor(k, actor)}
                      title={permissionLockedFor(k, actor) ? SUPERADMIN_ONLY_HINT : undefined}
                      onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })}
                      style={{ marginTop: 2 }}
                    />
                    <span style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ color: "var(--color-text)", fontWeight: 500 }}>
                        {getPermissionLabel(k)}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.65rem",
                          color: "var(--color-text-3)",
                          opacity: 0.7,
                        }}
                      >
                        {k}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </details>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded text-sm"
            style={{ border: "1px solid var(--color-border)" }}
          >
            Vazgeç
          </button>
          <button
            onClick={() => {
              const body: CreateRoleRequest = {
                name,
                permissions: perms,
                ...(description ? { description } : {}),
              };
              onCreate(body);
            }}
            disabled={name.trim().length < 2 || Object.values(perms).filter(Boolean).length === 0}
            className="px-3 py-1.5 rounded text-sm disabled:opacity-50"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            Oluştur
          </button>
        </div>
      </div>
    </div>
  );
}
